import React, { useState, useEffect } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { adminLogout, getUser, getAdmin, checkAdminTokenExpiration } from '../services/helpers'
import {
  HomeIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  TicketIcon,
  ShoppingBagIcon,
  TagIcon,
  CreditCardIcon,
  // ClipboardDocumentCheckIcon, // Removed Requests
  ArrowRightOnRectangleIcon,
  ExclamationTriangleIcon,
  DocumentChartBarIcon,
  UsersIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline'

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const navigate = useNavigate()
  const user = getUser()
  const admin = getAdmin()

  // Verify admin token on mount and periodically (only if not on login page)
  useEffect(() => {
    const currentPath = window.location.pathname
    const isLoginRoute = currentPath === '/admin/login'
    
    // Don't check token if we're on the login page
    if (isLoginRoute) {
      return
    }

    // Check immediately
    const verifyAuth = async () => {
      const admin = getAdmin()
      if (admin) {
        await checkAdminTokenExpiration()
      } else {
        // No admin found, redirect to login (but only if not already on login)
        const currentPath = window.location.pathname
        if (currentPath !== '/admin/login') {
          adminLogout()
        }
      }
    }

    verifyAuth()

    // Check every 5 minutes
    const interval = setInterval(() => {
      const currentPath = window.location.pathname
      if (currentPath !== '/admin/login') {
        verifyAuth()
      }
    }, 5 * 60 * 1000)

    // Also check on focus (when user returns to tab)
    const handleFocus = () => {
      const currentPath = window.location.pathname
      if (currentPath !== '/admin/login') {
        verifyAuth()
      }
    }
    window.addEventListener('focus', handleFocus)

    return () => {
      clearInterval(interval)
      window.removeEventListener('focus', handleFocus)
    }
  }, [])

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: HomeIcon, end: true },
    { name: 'Centers', href: '/admin/centers', icon: BuildingOfficeIcon },
    { name: 'Attendees', href: '/admin/attendees', icon: UserGroupIcon },
    { name: 'Events', href: '/admin/events', icon: CalendarDaysIcon },
    { name: 'Offers', href: '/admin/offers', icon: TagIcon },
    { name: 'Tickets', href: '/admin/tickets', icon: TicketIcon },
    { name: 'Orders', href: '/admin/orders', icon: ShoppingBagIcon },
    { name: 'Payments', href: '/admin/payments', icon: CreditCardIcon },
    { name: 'Payouts', href: '/admin/payouts', icon: ArrowRightOnRectangleIcon },
    { name: 'Disputes', href: '/admin/disputes', icon: ExclamationTriangleIcon },
    { name: 'Reports', href: '/admin/reports', icon: DocumentChartBarIcon },
    { name: 'System Users', href: '/admin/system-users', icon: UsersIcon },
    { name: 'Audit Log', href: '/admin/audit', icon: ShieldCheckIcon },
    { name: 'Profile', href: '/admin/profile', icon: UserCircleIcon },
  ]

  const handleLogout = () => {
    adminLogout()
    navigate('/admin/login')
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 bottom-0 left-0 z-50 w-64 bg-slate-900 border-r border-slate-800 text-white
        transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-800">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center text-white font-bold shadow-lg shadow-cyan-500/20">
              A
            </div>
            <span className="text-xl font-bold tracking-tight text-white">Obule Admin</span>
            <button onClick={() => setSidebarOpen(false)} className="ml-auto lg:hidden text-slate-400 hover:text-white">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Nav Items */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto custom-scrollbar">
            {navigation.map((item) => {
              const Icon = item.icon
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  end={item.end}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200
                    ${isActive
                      ? 'bg-cyan-600 text-white shadow-md shadow-cyan-900/20'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }
                  `}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {item.name}
                </NavLink>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 mb-3 px-2">
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-slate-300">
                <UserCircleIcon className="w-5 h-5" />
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-medium text-white truncate">{admin?.name || user?.name || 'Admin User'}</p>
                <p className="text-xs text-slate-500 truncate">{admin?.username || user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-slate-800 hover:text-red-300 rounded-lg transition-colors"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 h-16 flex items-center justify-between px-4 sticky top-0 z-30 shadow-sm">
          <button onClick={() => setSidebarOpen(true)} className="text-slate-500 hover:text-slate-900">
            <Bars3Icon className="w-6 h-6" />
          </button>
          <span className="font-semibold text-slate-900">Admin Console</span>
          <div className="w-6" />
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-slate-50 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default AdminLayout
